import { EditorView } from "@codemirror/view";
import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import { CodeEditorLanguage } from "@galaxy-io/dls/editor/CodeEditor";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import { useMediaQuery } from "@galaxy-io/dls/hooks/useMediaQuery";
import Flex, { AlignItems } from "@galaxy-io/dls/layout/Flex";
import ResizablePanels, { ResizablePanel } from "@galaxy-io/dls/layout/ResizablePanels";
import InfiniteTable, { InfiniteTableSize } from "@galaxy-io/dls/table/InfiniteTable";
import type { TableColumn } from "@galaxy-io/dls/table/types";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { Orientation } from "@galaxy-io/dls/theme/enums";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import {
  BookmarkSimpleIcon,
  DownloadSimpleIcon,
  PlayIcon,
  SidebarSimpleIcon,
} from "@phosphor-icons/react";
import { lazy, useMemo, useState } from "react";
import { queryHref, useUrlState } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/workspace/page-header";
import { Status } from "@/components/workspace/status";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { TableFooter } from "@/components/workspace/table-footer";
import { TableLayout } from "@/components/workspace/table-layout";
import {
  EditorPane,
  QueryBody,
  QueryMain,
  QueryWorkspace,
  ResultBody,
  Results,
} from "@/features/console/query-styles";
import { SavedQueriesDrawer } from "@/features/console/saved-queries-drawer";
import { SchemaExplorer } from "@/features/console/schema-explorer";
import { apiClient, unwrap } from "@/lib/api-client";
import { estimateColumnWidth } from "@/lib/table-layout";
import { toastDefaults } from "@/lib/toast";

// Lazy rather than Next's dynamic: there is no server render to opt out of,
// and the editor is heavy enough to be worth keeping out of the initial bundle.
const CodeEditor = lazy(() => import("@/components/ui/sql-editor"));
const editorExtensions = [
  EditorView.contentAttributes.of({ "aria-label": "SQL query" }),
  EditorView.lineWrapping,
];

import { InputVariant } from "@galaxy-io/dls/inputs/Input";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import Box, { BoxVariant } from "@galaxy-io/dls/layout/Box";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import { FloppyDiskIcon } from "@phosphor-icons/react";
import { useRouter } from "@/app/navigation";
import { Toolbar } from "@/components/workspace/toolbar";
import { csvCell } from "@/lib/csv";

type Result = {
  columns: string[];
  rows: unknown[][];
  truncated: boolean;
  elapsedMs: number;
};
const cell = (v: unknown) =>
  v === null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
// Long text values would otherwise auto-size a column past the viewport.
const MAX_COLUMN_WIDTH = 480;
export function QueryEditor({
  initialQuery,
  schema,
}: {
  initialQuery?: string;
  schema: Record<string, string[]>;
}) {
  const router = useRouter();
  const compact = useMediaQuery("(max-width: 640px)");
  const { params, update } = useUrlState();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const { toast } = useToast();
  const schemaOpen = params.get("schema") !== "hidden";
  const setSchemaOpen = (open: boolean) => update({ schema: open ? null : "hidden" });
  const [query, setQuery] = useState(initialQuery ?? "");
  const [result, setResult] = useState<Result | null>(null);
  const [resultQuery, setResultQuery] = useState("");

  const [busy, setBusy] = useState(false);
  // Saving creates a new entry in the saved-query library.
  async function save() {
    const label = name.trim();
    if (saving || !label || !query.trim()) return;
    setSaving(true);
    try {
      await unwrap(apiClient().POST("/console/queries", { body: { name: label, query } }));
      setSaveOpen(false);
      setName("");
      router.refresh();
      toast({
        variant: ToastVariant.SUCCESS,
        header: "Query saved",
        description: `“${label}” is under Saved queries.`,
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    } catch (e) {
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not save query",
        description: e instanceof Error ? e.message : "Try again.",
        ...toastDefaults(ToastVariant.ERROR),
      });
    } finally {
      setSaving(false);
    }
  }

  const columns = useMemo<TableColumn<unknown[]>[]>(
    () =>
      (result?.columns ?? []).map((name, index) => ({
        id: String(index),
        header: name,
        isRowHeader: index === 0,
        width: estimateColumnWidth(
          name,
          (result?.rows || []).map((row) => cell(row[index])),
          { maxWidth: MAX_COLUMN_WIDTH },
        ),
        maxWidth: Number.MAX_SAFE_INTEGER,
        accessor: (row) => row[index],
        cell: ({ value }) => (
          <div title={cell(value)}>
            <Text
              size={TextSize.BODY_SM}
              family={FontFamily.MONO}
              isSelectable
              lineClamp={1}
              variant={value === null ? TextVariant.TERTIARY : TextVariant.PRIMARY}
            >
              {value === null ? "null" : cell(value)}
            </Text>
          </div>
        ),
      })),
    [result],
  );
  async function run() {
    if (busy || !query.trim()) return;
    setBusy(true);

    setResult(null);
    try {
      const data = await unwrap(apiClient().POST("/console/query", { body: { query } }));
      setResult(data);
      setResultQuery(query);
    } catch (e) {
      toast({
        variant: ToastVariant.ERROR,
        header: "Query failed",
        description: e instanceof Error ? e.message : "Try again.",
        ...toastDefaults(ToastVariant.ERROR),
      });
    } finally {
      setBusy(false);
    }
  }
  function download() {
    if (!result) return;
    const csv = [result.columns, ...result.rows]
      .map((row) => row.map((v) => csvCell(cell(v))).join(","))
      .join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "galaxy-query.csv";
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <QueryWorkspace>
      <PageHeader
        title="Console"
        leading={
          <Button
            icon={SidebarSimpleIcon}
            ariaLabel="Toggle tables"
            aria-expanded={schemaOpen}
            variant={ButtonVariant.TERTIARY}
            size={ButtonSize.MEDIUM}
            onClick={() => setSchemaOpen(!schemaOpen)}
            tooltip="Toggle tables"
          />
        }
        actions={
          <Flex gap={8} alignItems={AlignItems.CENTER}>
            <Button
              label={compact ? undefined : "Saved queries"}
              ariaLabel="Saved queries"
              tooltip={compact ? "Saved queries" : undefined}
              icon={BookmarkSimpleIcon}
              size={ButtonSize.SMALL}
              variant={ButtonVariant.SECONDARY}
              onClick={() => setSavedOpen(true)}
            />
            <Button
              label={compact ? undefined : "Save"}
              ariaLabel="Save"
              tooltip={compact ? "Save query" : undefined}
              icon={FloppyDiskIcon}
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.SECONDARY}
              isDisabled={saving || !query.trim()}
              onClick={() => setSaveOpen(true)}
            />
            <Button
              onClick={run}
              isDisabled={busy || !query.trim()}
              icon={PlayIcon}
              size={ButtonSize.MEDIUM}
              label={busy ? "Running…" : "Run query"}
            />
          </Flex>
        }
      />
      <SavedQueriesDrawer
        open={savedOpen}
        onClose={() => setSavedOpen(false)}
        activeId={params.get("id")}
        onActiveDeleted={() => {
          setSavedOpen(false);
          router.replace(queryHref("/query", params, { id: null, sql: query }));
        }}
      />
      <SurfaceDialog
        title="Save query"
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        busy={saving}
        footer={
          <Button
            label="Save query"
            isDisabled={saving || !name.trim()}
            isLoading={saving}
            onClick={() => void save()}
          />
        }
      >
        <TextInput
          label="Name"
          ref={(input) => input?.setAttribute("aria-label", "Query name")}
          placeholder="Untitled query"
          value={name}
          onChange={setName}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void save();
            }
          }}
          autoFocus
          fillWidth
          variant={InputVariant.BASE}
          isDisabled={saving}
        />
      </SurfaceDialog>
      <QueryBody>
        {schemaOpen && (
          <SchemaExplorer
            schema={schema}
            onSelect={(name) =>
              router.push(
                queryHref("/query", params, {
                  dataset: name,
                  id: null,
                  preset: null,
                  new: null,
                }),
                { scroll: false },
              )
            }
          />
        )}
        <QueryMain>
          <ResizablePanels orientation={Orientation.VERTICAL}>
            <ResizablePanel
              id="query-editor"
              ariaLabel="Query editor"
              defaultValue={window.innerHeight * 0.36}
              min={160}
              max={window.innerHeight * 0.6}
            >
              <EditorPane
                aria-label="Query editor pane"
                onKeyDownCapture={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                    event.preventDefault();
                    event.stopPropagation();
                    void run();
                  }
                }}
              >
                <Flex height="100%" fillWidth minWidth={0} alignItems={AlignItems.STRETCH}>
                  <CodeEditor
                    placeholder="Write a query…"
                    isReadOnly={saving}
                    value={query}
                    onChange={setQuery}
                    language={CodeEditorLanguage.SQL}
                    extensions={editorExtensions}
                  />
                </Flex>
              </EditorPane>
            </ResizablePanel>
            <ResizablePanel id="query-results" min={160}>
              <Results aria-label="Results">
                <Toolbar
                  trailingActions={
                    result && (
                      <Button
                        onClick={download}
                        variant={ButtonVariant.TERTIARY}
                        size={ButtonSize.SMALL}
                        icon={DownloadSimpleIcon}
                        label="Export results"
                      />
                    )
                  }
                >
                  <Flex gap={8} alignItems={AlignItems.CENTER}>
                    <Text size={TextSize.BODY_SM}>Results</Text>
                    {result && <Status value="succeeded" label="Complete" />}
                    {result && (
                      <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                        {result.elapsedMs} ms
                        {query !== resultQuery ? " · Query changed" : ""}
                      </Text>
                    )}
                  </Flex>
                </Toolbar>
                {result?.truncated && (
                  <Flex alignItems={AlignItems.START} padding={[8, 12]}>
                    <Text size={TextSize.BODY_SM}>
                      Limited to 1,000 rows. Narrow your query for a complete export.
                    </Text>
                  </Flex>
                )}
                <ResultBody>
                  {result ? (
                    <TableLayout
                      footer={
                        <TableFooter
                          label="Query results"
                          total={result.rows.length}
                          totalTruncated={result.truncated}
                        />
                      }
                    >
                      <Box variant={BoxVariant.BASE} height="100%">
                        <InfiniteTable
                          ariaLabel="Query results"
                          columns={columns}
                          data={result.rows}
                          getRowId={(_, index) => String(index)}
                          canResizeColumns
                          size={InfiniteTableSize.SMALL}
                          emptyState={<EmptyState header="Query completed. No matching rows" />}
                        />
                      </Box>
                    </TableLayout>
                  ) : (
                    <Flex alignItems={AlignItems.START}>
                      <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY}>
                        {busy ? "Running…" : "Run a query to see results."}
                      </Text>
                    </Flex>
                  )}
                </ResultBody>
              </Results>
            </ResizablePanel>
          </ResizablePanels>
        </QueryMain>
      </QueryBody>
    </QueryWorkspace>
  );
}
